import { IHeartbeatMonitor } from "./interfaces";
import { ErrorHandler } from "../error.handler.service";
import ky from "ky";
import { AppConfigService } from "../app.config.service";
import { IConversation } from "@auvious/integrations";
import { localStore, sessionStore } from "@auvious/utils";

// this is a uuid used by web messaging integration to reconnect to same session
const KEY_SESSION_TOKEN = "auvious.session.token";
const KEY_HEARTBEAT_SESSION = "auvious.heartbeat.";
enum HeartbeatState {
  beat = "beat",
  stop = "stop",
}

export class GenesysWebMessagingHeartbeatMonitor implements IHeartbeatMonitor {
  private _id: string;
  private _retryCount = 3;
  private _sessionToken: string;
  private _monitorExists = false;

  public async createMonitor(
    conversation: IConversation,
    timeoutSeconds: number
  ) {
    if (!conversation?.id || this._monitorExists) return;

    this._id = conversation.id;
    this._sessionToken = localStore.getItem(KEY_SESSION_TOKEN);

    // try to find if we have already started a heartbeat and stopped in the past (maybe a page refresh?)
    // we don't know if the agent is actually connected so we always try to set up this heartbeat
    const state = sessionStore.getItem(
      KEY_HEARTBEAT_SESSION + this._sessionToken
    );
    if (state === HeartbeatState.stop) return;

    try {
      await this.retryRequest(
        AppConfigService.applicationId,
        conversation.id,
        timeoutSeconds,
        this._retryCount,
        100
      ); // Retry after 500 milliseconds

      sessionStore.setItem(
        KEY_HEARTBEAT_SESSION + this._sessionToken,
        HeartbeatState.beat
      );

      this._monitorExists = true;
    } catch (ex) {
      ErrorHandler.captureError(ex);
      throw ex;
    }
  }

  private async retryRequest(
    applicationId: string,
    interactionId: string,
    timeoutSeconds: number,
    retriesLeft: number,
    delay: number
  ): Promise<void> {
    // api/genesys/monitor/interaction
    try {
      await ky
        .post(
          `${AppConfigService.auviousBaseUrl}/api/genesys/monitor/interaction`,
          {
            json: {
              applicationId,
              interactionId,
              timeoutSeconds,
            },
          }
        )
        .json();
    } catch (ex) {
      if (
        retriesLeft > 0 &&
        ex.response &&
        ex.response.status >= 500 &&
        ex.response.status < 600
      ) {
        await new Promise((resolve) => setTimeout(resolve, delay)); // Wait before retrying
        // Increase delay for next retry
        const nextDelay = delay * 2;
        // Retry the request recursively with one less retry count and increased delay
        await this.retryRequest(
          applicationId,
          interactionId,
          timeoutSeconds,
          retriesLeft - 1,
          nextDelay
        );
      } else {
        throw ex;
      }
    }
  }

  public cleanup() {
    sessionStore.removeItem(KEY_HEARTBEAT_SESSION + this._sessionToken);
    this._sessionToken = undefined;
    this._id = undefined;
  }

  public async destroyMonitor() {
    try {
      if (!this._id) return;

      // update state in local store
      if (this._sessionToken) {
        sessionStore.setItem(
          KEY_HEARTBEAT_SESSION + this._sessionToken,
          HeartbeatState.stop
        );
      }

      this._monitorExists = false;

      await ky.delete(
        `${AppConfigService.auviousBaseUrl}/api/genesys/monitor/interaction/${this._id}`
      );
    } catch (ex) {
      ErrorHandler.captureError(ex);
      throw ex;
    }
  }

  public async beat() {
    try {
      if (!this._id) return;

      await ky.post(
        `${AppConfigService.auviousBaseUrl}/api/genesys/monitor/interaction/${this._id}/heartbeat`
      );
    } catch (ex) {
      ErrorHandler.captureError(ex);
      throw ex;
    }
  }
}
